/**
 * Catalog — items grid with filters, and the components of the selected item.
 *
 * Functional spec: `qt_poc/catalog_qt.py`. Same master/detail layout, same
 * columns in the same order, same Hebrew headers, same filter cascade.
 *
 * Two things copied deliberately rather than simplified:
 *
 *   - The description column shows the **first component's** description. Item
 *     names are frequently opaque manufacturer SKUs (`91320007`), so without
 *     it you cannot tell what a row actually is.
 *   - Prices come from the server's price context in the dealer's **native
 *     currency**, often ILS. They are not USD. The old catalog read
 *     `unit_price_usd` as USD and converted again, producing doubly-wrong
 *     numbers; the currency travels with the value here so that cannot recur.
 */
import { useMemo, useState } from 'react'
import {
  ActionIcon, Button, Checkbox, Group, Menu, Paper, Select, Stack, Text,
  TextInput, Title, Tooltip,
} from '@mantine/core'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { ItemDetail, ItemRow, MasterData } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { DataGrid } from '../components/DataGrid'
import { COMPACT_QUERY, FilterPanel } from '../components/FilterPanel'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtMoney, fmtPct, fmtQty } from '../format'
import { FILL_PANEL } from '../theme'
import { confirmDelete } from '../components/confirm'
import { BulkEditModal } from './BulkEditModal'
import { CatalogImportModal } from './CatalogImport'
import { ItemEditor } from './ItemEditor'

const ANY = ''

export function CatalogPage() {
  const { canEdit, canDelete, isViewer } = useAuth()
  // Same threshold `FilterPanel` collapses its own selects at — used here for
  // the *surrounding* chrome (`Stack` gap, the search field's label) rather
  // than inside it, which is why it is a second hook call on the one shared
  // constant rather than something `FilterPanel` could decide on its own.
  const compact = useMediaQuery(COMPACT_QUERY)
  const [editing, setEditing] = useState<{ open: boolean; id: number | null }>(
    { open: false, id: null },
  )
  const [search, setSearch] = useState('')
  // Debounced so a keystroke does not fire a query per character against a
  // 6,500-row catalog.
  const [debounced] = useDebouncedValue(search, 300)
  const [manufacturer, setManufacturer] = useState<string | null>(ANY)
  const [family, setFamily] = useState<string | null>(ANY)
  const [status, setStatus] = useState<string | null>(ANY)
  const [dealer, setDealer] = useState<string | null>(ANY)
  const [selected, setSelected] = useState<ItemRow | null>(null)
  // Ticked rows, for the two bulk actions. Separate from `selected`, which
  // drives the components pane below and is always exactly one row.
  const [ticked, setTicked] = useState<Set<number>>(new Set())
  const [bulkEditing, setBulkEditing] = useState(false)
  // The wizard also lives in Settings, but the catalog is where someone stands
  // when they notice the catalog is missing something.
  const [importing, setImporting] = useState(false)

  const queryClient = useQueryClient()

  const master = useQuery({
    queryKey: ['master-data'],
    queryFn: () => api.get<MasterData>('/master-data'),
  })

  const items = useQuery({
    queryKey: ['items', debounced, manufacturer, status, dealer],
    queryFn: () => {
      const params = new URLSearchParams()
      if (debounced) params.set('q', debounced)
      if (manufacturer) params.set('manufacturer', manufacturer)
      if (status) params.set('sale_status', status)
      if (dealer) params.set('dealer', dealer)
      const qs = params.toString()
      return api.get<ItemRow[]>(`/items${qs ? `?${qs}` : ''}`)
    },
  })

  // Family is filtered client-side: the API filters by manufacturer/status/
  // dealer, and adding a fourth server parameter for a list already in memory
  // would cost a round trip per toggle.
  const rows = useMemo(
    () => (family ? (items.data ?? []).filter((i) => i.family === family) : items.data ?? []),
    [items.data, family],
  )

  /**
   * Delete every ticked item — `🗑 מחק נבחרים` on the desktop.
   *
   * One request each, and it keeps going past a failure rather than stopping:
   * the usual reason a catalog item will not delete is that a BOM references
   * it, which says nothing about the next item in the list. The count of both
   * outcomes is reported.
   */
  async function removeTicked() {
    const ids = [...ticked]
    if (!ids.length) return
    if (!(await confirmDelete(`${ids.length} פריטים`))) return
    let deleted = 0
    const failures: string[] = []
    for (const id of ids) {
      try {
        await api.delete(`/items/${id}`)
        deleted += 1
      } catch (err) {
        failures.push(err instanceof ApiError ? err.message : String(id))
      }
    }
    setTicked(new Set())
    setSelected(null)
    void queryClient.invalidateQueries({ queryKey: ['items'] })
    notifications.show({
      color: failures.length ? 'yellow' : 'green',
      message: failures.length
        ? `נמחקו ${deleted}; ${failures.length} נכשלו — ${failures[0]}`
        : `נמחקו ${deleted} פריטים`,
    })
  }

  /** The catalog as Excel, narrowed by whatever is filtering the grid. */
  async function exportExcel() {
    const params = new URLSearchParams()
    if (manufacturer) params.set('manufacturer', manufacturer)
    if (status) params.set('sale_status', status)
    const qs = params.toString()
    try {
      await api.download(`/items/export.xlsx${qs ? `?${qs}` : ''}`,
                         'catalog_export.xlsx')
    } catch (err) {
      // A filter matching nothing is a 404 with a Hebrew message, which is
      // more useful than an empty workbook that reads as "the catalog is
      // empty". Note the family filter is client-side and does NOT narrow
      // this — the server does not know about it.
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'הייצוא נכשל',
      })
    }
  }

  async function removeItem() {
    if (!selected?.id) return
    if (!(await confirmDelete(`הפריט "${selected.name}"`))) return
    try {
      await api.delete(`/items/${selected.id}`)
      notifications.show({ message: 'הפריט נמחק', color: 'green' })
      setSelected(null)
      void queryClient.invalidateQueries({ queryKey: ['items'] })
    } catch (err) {
      notifications.show({
        message: err instanceof ApiError ? err.message : 'מחיקת הפריט נכשלה',
        color: 'red',
      })
    }
  }

  const detail = useQuery({
    queryKey: ['item', selected?.id],
    queryFn: () => api.get<ItemDetail>(`/items/${selected!.id}`),
    enabled: selected?.id != null,
  })

  /**
   * A money cell a viewer cannot see (DP-01): the dash, same as any other
   * missing value, but with a tooltip saying *why* it is missing rather than
   * leaving it looking like the item simply has no price at all.
   */
  function hiddenPrice() {
    return (
      <Tooltip label="מוצג למנהלים ולעורכים בלבד">
        <Text span c="dimmed" style={{ cursor: 'help' }}>{EM_DASH}</Text>
      </Tooltip>
    )
  }

  const columns = useMemo<ColumnDef<ItemRow, any>[]>(
    () => [
      ...(canEdit ? [{
        id: 'tick',
        header: '',
        // Widths are declared because this grid windows itself at catalog size;
        // see ColumnMeta.width in DataGrid.
        meta: { width: 46 },
        cell: (c: any) => (
          <Checkbox
            aria-label="בחר פריט"
            size="md"
            checked={ticked.has(c.row.original.id!)}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              setTicked((prev) => {
                const next = new Set(prev)
                const id = c.row.original.id!
                if (next.has(id)) next.delete(id)
                else next.add(id)
                return next
              })
            }
          />
        ),
      } as ColumnDef<ItemRow, any>] : []),
      { accessorKey: 'name', header: 'מק"ט יצרן', meta: { code: true, width: 130 } },
      {
        accessorKey: 'description',
        header: 'תיאור',
        meta: { width: 290 },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      {
        id: 'internal',
        header: 'מק"ט ארגוני',
        accessorFn: (r) => r.internal_sku,
        // Hidden first, per the brief's own example for this exact grid: the
        // identifying SKU, description and net price are what the catalog is
        // for, so they stay; this is a secondary identifier most rows don't
        // even have (EM_DASH below).
        meta: { code: true, width: 90, hideBelow: 'lg' },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      { accessorKey: 'manufacturer', header: 'יצרן', meta: { width: 100, hideBelow: 'md' } },
      {
        id: 'dealer',
        header: 'דילר',
        accessorFn: (r) => r.dealer_names.join(', '),
        meta: { width: 70, hideBelow: 'md' },
        cell: (c) => c.getValue<string>() || EM_DASH,
      },
      { accessorKey: 'family', header: 'משפחה', meta: { width: 110, hideBelow: 'lg' } },
      {
        accessorKey: 'sale_status',
        header: 'סטטוס',
        meta: { nowrap: true, width: 88, hideBelow: 'lg' },
        // Use the shared StatusBadge rather than a local <Badge>: it owns the
        // colour vocabulary (so ACTIVE means the same green everywhere) and the
        // fix that stops Mantine ellipsising the label into `…TIVE`.
        cell: (c) => <StatusBadge value={c.getValue<string>()} />,
      },
      {
        accessorKey: 'component_count',
        header: '#',
        meta: { numeric: true, width: 44, hideBelow: 'md' },
        cell: (c) => fmtQty(c.getValue<number>()),
      },
      {
        id: 'list',
        header: 'מחיר רשימה',
        meta: { numeric: true, width: 100, hideBelow: 'md' },
        accessorFn: (r) => r.list_price,
        cell: (c) => {
          const row = c.row.original
          if (row.list_price != null && row.list_price > 0) {
            return fmtMoney(row.list_price, row.price_currency)
          }
          // Null (not 0) is how a viewer's redacted price arrives (DP-01) —
          // `null > 0` above is already false, same as a real non-positive
          // price, so the only thing left to decide is which dash to show.
          return isViewer ? hiddenPrice() : EM_DASH
        },
      },
      {
        id: 'net',
        header: 'מחיר נטו',
        meta: { numeric: true, width: 100 },
        accessorFn: (r) => r.effective_price,
        cell: (c) => {
          const row = c.row.original
          if (row.effective_price != null && row.effective_price > 0) {
            return fmtMoney(row.effective_price, row.price_currency)
          }
          return isViewer ? hiddenPrice() : EM_DASH
        },
      },
    ],
    [canEdit, ticked, isViewer],
  )

  const componentColumns = useMemo<ColumnDef<ItemDetail['components'][number], any>[]>(
    () => [
      { accessorKey: 'sku', header: 'מקט יצרן', meta: { code: true } },
      { accessorKey: 'internal_sku', header: 'מקט פנימי', meta: { code: true }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'description', header: 'תיאור' },
      { accessorKey: 'component_type', header: 'סוג', cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'default_qty_ratio', header: 'יחס', meta: { numeric: true }, cell: (c) => fmtQty(c.getValue<number>()) },
      {
        accessorKey: 'unit_price_usd',
        header: 'GPL',
        meta: { numeric: true },
        // Header says GPL rather than a currency symbol on purpose: this field
        // holds the dealer's native currency for imported items, so labelling
        // it "$" would be a lie for most of the catalog.
        //
        // Null for a viewer (DP-01) — `fmtQty` already renders that as the
        // dash; the tooltip on top just says why it is a dash.
        cell: (c) => {
          const value = c.getValue<number | null>()
          return value == null && isViewer ? hiddenPrice() : fmtQty(value)
        },
      },
      { accessorKey: 'discount_pct', header: 'הנחה%', meta: { numeric: true }, cell: (c) => fmtPct(c.getValue<number>() * 100) },
      {
        id: 'netc',
        header: 'מחיר נטו',
        meta: { numeric: true },
        // The null must propagate, not multiply: `unit_price_usd` is null for
        // a viewer (DP-01), and `null * x` coerces to a real `0` in JS — that
        // would show a component "costing" 0, which reads as free, instead of
        // the redaction it actually is.
        accessorFn: (r) =>
          r.unit_price_usd == null ? null : r.unit_price_usd * (1 - (r.discount_pct || 0)),
        cell: (c) => {
          const value = c.getValue<number | null>()
          return value == null && isViewer ? hiddenPrice() : fmtQty(value)
        },
      },
      { accessorKey: 'sale_status', header: 'סטטוס', cell: (c) => c.getValue<string>() || EM_DASH },
    ],
    [isViewer],
  )

  const statuses = ['ACTIVE', 'EOS', 'EOL']

  return (
    // `h="100%"` + `minHeight: 0` is what lets the grid below actually fill
    // the remaining viewport height instead of sizing to a fixed vh guess —
    // see `FILL_PANEL`'s own comment in theme.ts for the rest of the chain.
    // `gap` tightens with everything else below `compact`: three gaps at the
    // default "md" (16px) versus 8px is 24px back, on a screen that was
    // short 8.8px of the 12-row floor at 1366x768 before this.
    <Stack gap={compact ? 8 : 'md'} h="100%" style={{ minHeight: 0 }}>
      <Group justify="space-between">
        <Title order={2}>קטלוג פריטים</Title>
        <Group gap="xs">
          <Text size="sm" c="dimmed">{rows.length.toLocaleString('he-IL')} פריטים</Text>
          {canEdit && (
            <Button onClick={() => setEditing({ open: true, id: null })}>פריט חדש</Button>
          )}
          {canEdit && selected && (
            <Button variant="light" onClick={() => setEditing({ open: true, id: selected.id })}>
              ערוך
            </Button>
          )}
          {canDelete && selected && (
            <Button variant="light" color="red" onClick={() => void removeItem()}>
              מחק
            </Button>
          )}
          {ticked.size > 0 && (
            <>
              <Text size="sm" c="dimmed">{ticked.size} מסומנים</Text>
              {canEdit && ticked.size >= 2 && (
                <Button variant="light" onClick={() => setBulkEditing(true)}>
                  ערוך מרובה
                </Button>
              )}
              {canDelete && (
                <Button variant="light" color="red" onClick={() => void removeTicked()}>
                  מחק נבחרים
                </Button>
              )}
              <Button variant="subtle" onClick={() => setTicked(new Set())}>
                בטל בחירה
              </Button>
            </>
          )}
          {/* Excel in and out, matching the desktop's "ייבוא / ייצוא ⋯" menu.
              The export carries the filters currently on screen — exporting
              the whole catalog while the user is looking at one manufacturer
              is not what the button appears to promise. */}
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant="default">ייבוא / ייצוא</Button>
            </Menu.Target>
            <Menu.Dropdown>
              {canEdit && (
                <Menu.Item onClick={() => setImporting(true)}>
                  ייבא מ-Excel…
                </Menu.Item>
              )}
              <Menu.Item onClick={() => void exportExcel()}>
                ייצא ל-Excel (לפי הסינון)
              </Menu.Item>
              <Menu.Item
                onClick={() => void api.download(
                  '/items/import-template.xlsx', 'catalog_import_template.xlsx')}
              >
                הורד תבנית ייבוא
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      <FilterPanel
        storageKey="catalog"
        search={
          <TextInput
            // No `label` when compact: the placeholder already says
            // "חיפוש…", and the label's own line was part of the ~9px this
            // screen was short of 12 visible rows at 1366x768 — small on its
            // own, real once added to the `Stack` gap and padding trims
            // alongside it.
            label={compact ? undefined : 'חיפוש'}
            placeholder="חיפוש…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ minWidth: 240 }}
          />
        }
        extra={
          <Tooltip label="נקה מסננים">
            <ActionIcon
              variant="default" size={compact ? 'md' : 'lg'}
              onClick={() => {
                setSearch(''); setManufacturer(ANY); setFamily(ANY)
                setStatus(ANY); setDealer(ANY)
              }}
            >
              ✕
            </ActionIcon>
          </Tooltip>
        }
      >
        <Select
          label="יצרן" clearable clearButtonProps={{ "aria-label": "נקה", size: "md" }} searchable placeholder="הכל"
          data={master.data?.manufacturers ?? []}
          value={manufacturer} onChange={setManufacturer} style={{ minWidth: 160 }}
        />
        <Select
          label="משפחה" clearable clearButtonProps={{ "aria-label": "נקה", size: "md" }} searchable placeholder="הכל"
          data={master.data?.families ?? []}
          value={family} onChange={setFamily} style={{ minWidth: 160 }}
        />
        <Select
          label="דילר" clearable clearButtonProps={{ "aria-label": "נקה", size: "md" }} searchable placeholder="הכל"
          data={(master.data?.dealers ?? []).map((d) => d.name)}
          value={dealer} onChange={setDealer} style={{ minWidth: 160 }}
        />
        <Select
          label="סטטוס" clearable clearButtonProps={{ "aria-label": "נקה", size: "md" }} placeholder="הכל"
          data={statuses} value={status} onChange={setStatus} style={{ minWidth: 130 }}
        />
      </FilterPanel>

      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={rows}
          columns={columns}
          loading={items.isLoading}
          empty="לא נמצאו פריטים התואמים את המסננים"
          onRowClick={setSelected}
          selected={selected}
          maxHeight="100%"
          minWidth={1180}
        />
      </Paper>

      {selected ? (
        <Paper withBorder p="sm">
          <Group justify="space-between" mb="xs">
            <Title order={5}>רכיבים — {selected.name}</Title>
            {detail.data && detail.data.dealer_names.length > 0 && (
              <Text size="sm" c="dimmed">
                דילרים: {detail.data.dealer_names.join(', ')}
              </Text>
            )}
          </Group>
          <DataGrid
            data={detail.data?.components ?? []}
            columns={componentColumns}
            loading={detail.isLoading}
            empty="לפריט זה אין רכיבים"
            maxHeight="30vh"
            stickyFirstColumn={false}
          />
        </Paper>
      ) : (
        // A one-line hint, not a titled Paper with nothing in it: on a short
        // viewport (DP-11) a bordered panel's worth of chrome for "nothing is
        // selected yet" was most of the headroom the grid above needed to
        // clear the 12-row floor — measured costing ~113px at 1366x768 versus
        // this line's ~20px. The full panel still appears the instant a row
        // is selected, which is the only state where it has anything to show.
        <Text c="dimmed" size="sm">בחר פריט מהטבלה למעלה כדי לראות את רכיביו.</Text>
      )}

      <ItemEditor
        opened={editing.open}
        itemId={editing.id}
        master={master.data}
        onClose={() => setEditing({ open: false, id: null })}
      />
      <CatalogImportModal opened={importing} onClose={() => setImporting(false)} />
      <BulkEditModal
        ids={[...ticked]}
        master={master.data}
        opened={bulkEditing}
        onClose={() => { setBulkEditing(false); setTicked(new Set()) }}
      />
    </Stack>
  )
}
